import type { ReactNode } from 'react';

import { configuration } from '@src/entities/Alert/includes/configuration';
import { AlertEntity, DefaultListAlertsPayload, ListAlertsPayload } from '@src/entities/Alert/includes/types';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { Table as TableComponent } from '@src/shared/ui/components/TableQuery';
import { dayjs } from '@uhg-abyss/web/tools/dayjs';
import { Badge } from '@uhg-abyss/web/ui/Badge';
import { DropdownMenu } from '@uhg-abyss/web/ui/DropdownMenu';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { ToggleSwitch } from '@uhg-abyss/web/ui/ToggleSwitch';
import { Tooltip } from '@uhg-abyss/web/ui/Tooltip';
import { isUndefined, orderBy } from 'lodash';
import React, { useMemo } from 'react';

import tableConfiguration from './includes/configuration.json';
import { Styles } from './includes/styles';

type TableColumn = {
  [key: string]: unknown;
  accessor: string;
  Cell?: (props: any) => ReactNode;
  Header: string;
  order?: number;
};

type AlertsProps = {
  error?: unknown;
  headerLeft?: ReactNode;
  isLoading?: boolean;
  requestArgs?: ListAlertsPayload;
  requestFunction?: (...args: any[]) => void;
  requestKey?: string;
  rows?: AlertEntity[];
  setCurrentEntity?: (entity: AlertEntity) => void;
  setShowAll?: (checked: boolean) => void;
  setShowModal?: (show: boolean) => void;
  showAll?: boolean;
  totalPages?: number;
  totalRecords?: number;
};

/**
 * Table: Alerts
 *
 * Displays a list of alerts within a DataTable.
 */
export function Table({
  error = null,
  isLoading = false,
  requestArgs = DefaultListAlertsPayload,
  requestFunction = () => {},
  requestKey = '',
  rows = [],
  setCurrentEntity = () => {},
  setShowAll = () => {},
  setShowModal = () => {},
  showAll = false,
  totalPages = 1,
  totalRecords = 0,
}: AlertsProps) {
  /**
   * Renders the "Last Updated" cell as a formatted date string.
   */
  const renderCellLastUpdated = ({ cell }: { cell: { value?: Date | number | string } }): string => {
    return cell?.value ? dayjs(cell.value).format('MM/DD/YYYY, HH:mm:ss') : '';
  };

  /**
   * Renders the "Status" cell as a Badge if the value matches a known status, otherwise as a string.
   */
  const renderCellStatus = ({ cell }: { cell: { value?: string } }): ReactNode | string => {
    const badges: Record<string, 'neutral' | 'success' | 'warning'> = {
      assigned: 'success',
      closed: 'neutral',
      triggered: 'warning',
    };

    const value = cell?.value?.toLowerCase() ?? '';
    if (!isUndefined(badges[value])) {
      return (
        <Badge css={{ lineHeight: 1 }} outline variant={badges[value]}>
          <span style={{ textTransform: 'capitalize' }}>{value}</span>
        </Badge>
      );
    }

    return cell?.value ?? '';
  };

  /**
   * Renders the "Actions" cell with a dropdown menu for row actions.
   */
  const renderCellActions = ({ row }: { row: { original: AlertEntity } }): ReactNode => {
    const menuItems = [
      {
        icon: <IconSymbol icon="edit" />,
        onClick: () => {
          setShowModal(true);
          setCurrentEntity(row.original);
        },
        title: 'Edit',
      },
    ];

    return (
      <div
        style={{
          marginLeft: '-10px',
          marginRight: '-10px',
        }}
      >
        <Layout.Group
          css={{
            height: '100%',
            marginLeft: '2px',
            marginRight: '2px',
          }}
        >
          <DropdownMenu
            before={<IconSymbol color="#6F6F6F" icon="more_vert" size={24} />}
            hideLabel
            menuItems={menuItems}
          />
        </Layout.Group>
      </div>
    );
  };

  /**
   * Renders the "Description" cell with a tooltip for full content.
   */
  const renderCellDescription = ({ cell }: { cell: { value?: string } }): ReactNode => {
    return (
      <Tooltip
        content={
          <div
            style={{
              fontFamily: 'var(--abyss-fonts-primary)',
              padding: 'var(--abyss-space-md)',
            }}
          >
            {cell?.value}
          </div>
        }
      >
        <span className="truncate">{cell?.value}</span>
      </Tooltip>
    );
  };

  /**
   * Columns configuration for the table.
   * Custom cell renderers are assigned for specific columns; others display their values by default as-is.
   */
  const columns: TableColumn[] = useMemo(() => {
    return orderBy(tableConfiguration?.initialColumns, ['order'], ['asc']).map((column: TableColumn) => {
      const theColumn = { ...column };

      if (theColumn.Header === 'Last Updated') {
        theColumn.Cell = renderCellLastUpdated;
      }

      if (theColumn.Header === 'Status') {
        theColumn.Cell = renderCellStatus;
      }

      if (theColumn.Header === 'Description') {
        theColumn.Cell = renderCellDescription;
      }

      if (theColumn.accessor === 'actions') {
        theColumn.Cell = renderCellActions;
      }

      return theColumn;
    });
  }, []);

  return (
    <ErrorHandler location="src/entities/Alert/ui/components/Table/Table.tsx">
      <Styles>
        <TableComponent
          columns={columns}
          configuration={tableConfiguration}
          error={error}
          headerLeft={
            <ToggleSwitch
              isChecked={showAll}
              label="Show Closed Alerts"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setShowAll(event?.target?.checked);
              }}
            />
          }
          isLoading={isLoading}
          noDataMessage={`No ${configuration.pluralName} Found.`}
          requestArgs={requestArgs}
          requestFunction={requestFunction}
          requestKey={requestKey}
          rows={rows}
          totalPages={totalPages}
          totalRecords={totalRecords}
        />
      </Styles>
    </ErrorHandler>
  );
}
